import React, { FC, useEffect, useMemo, useState } from 'react';
import { PageVersion } from '@/declarations/models/PageVersion';
import { Box, List, ListItemButton, Menu, MenuItem, Typography } from '@mui/material';
import { Api } from '@/services/Api';
import { useSearchParams } from 'react-router-dom';
import { formatAPITimestamp, isApiTimestampAtOrAfter } from '@/utils/dates';
import { useTranslation } from 'react-i18next';
import Styles from '@/assets/js/Styles';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { PageStatusCircle } from '@/components/PageStatusCircle';
import { Page } from '@/declarations/models/Page';
import { Status } from '@/declarations/models/Status';
import { useEditorModel } from '../lib/components/EditorDataProvider';
import EditorHeader, { EditorHeaderProps } from '../lib/components/EditorHeader/EditorHeader';
import { usePageEditorContext } from './PageEditorContext';

type DisplayStatus = Status | 'planned_publish';

const getDisplayStatus = (page: Page): DisplayStatus => {
  if (page.status === Status.PUBLISHED) {
    const isPlannedPublish = !!page.published_at && isApiTimestampAtOrAfter(page.published_at);

    if (isPlannedPublish) {
      return 'planned_publish';
    }
  }

  return page.status;
};

/**
 * Tells the editor why an imported page cannot be published from here: it has to
 * be placed in the page tree first (ADR-0013). Renders nothing for any other status.
 */
export const ImportedPageHint: FC<{ status?: Status }> = ({ status }) => {
  const { t: tComponents } = useTranslation('components');
  if (status !== Status.IMPORTED) {
    return null;
  }
  return (
    <Typography
      role='note'
      variant='body2'
      sx={{ color: Styles.Colors.DARK_GREY, display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
      <MaterialSymbol name='info' sx={{ fontSize: 18 }} />
      {tComponents('EditorHeader.ImportedPlaceHint')}
    </Typography>
  );
};

export const VersionBox: FC<{ pageStatus: DisplayStatus; publishDate?: string; version?: PageVersion }> = ({
  pageStatus,
  publishDate,
  version,
}) => {
  const { t: tCommon } = useTranslation('common');

  const iconIndicator = () => {
    if (pageStatus === 'planned_publish' && version?.status === Status.PUBLISHED) {
      return <PageStatusCircle status='planned_publish' />;
    }
    if (pageStatus === Status.PUBLISHED && version?.status === Status.PUBLISHED) {
      return <PageStatusCircle status={Status.PUBLISHED} />;
    }
    if (pageStatus === Status.ARCHIVED && version?.status === Status.PUBLISHED) {
      return <PageStatusCircle status={Status.ARCHIVED} />;
    }
    if (pageStatus === Status.DRAFT && version?.status === Status.PUBLISHED) {
      return <PageStatusCircle status={Status.DRAFT} />;
    }
    if (pageStatus === Status.IMPORTED && version?.status === Status.PUBLISHED) {
      return <PageStatusCircle status={Status.IMPORTED} />;
    }
    return (
      <MaterialSymbol
        name='format_paint'
        sx={{
          fontSize: 18,
        }}
      />
    );
  };

  const labelColor = () => {
    if (!version) {
      return Styles.Colors.DARK_GREY;
    }
    if (pageStatus === 'planned_publish' && version.status === Status.PUBLISHED) {
      return Styles.Colors.STRONG_ORANGE;
    }
    if (pageStatus === Status.PUBLISHED && version.status === Status.PUBLISHED) {
      return Styles.Colors.STRONG_GREEN;
    }
    if (pageStatus === Status.ARCHIVED && version.status === Status.PUBLISHED) {
      return Styles.Colors.MEDIUM_BLUE;
    }
    if (pageStatus === Status.DRAFT && version.status === Status.PUBLISHED) {
      return Styles.Colors.DARK_GREY;
    }
    return Styles.Colors.DARK_GREY;
  };

  const label = () => {
    if (!version) {
      return tCommon('Status.no_draft');
    }
    if (pageStatus === 'planned_publish' && version.status === Status.PUBLISHED) {
      return `${tCommon('Status.planned_publishing')} ${publishDate || ''}`;
    }
    if (pageStatus === Status.PUBLISHED && version.status === Status.PUBLISHED) {
      return `${tCommon('Status.published')} ${publishDate || ''}`;
    }
    if (pageStatus === Status.ARCHIVED && version.status === Status.PUBLISHED) {
      return tCommon('Status.archived');
    }
    if (pageStatus === Status.DRAFT && version.status === Status.PUBLISHED) {
      return tCommon('Status.not_published');
    }
    if (pageStatus === Status.IMPORTED && version.status === Status.PUBLISHED) {
      return tCommon('Status.imported');
    }
    return tCommon('Status.draft');
  };

  const lastEditedAt = () => {
    if (!version) {
      return null;
    }
    return (
      <Typography sx={{ fontSize: 10, marginTop: 0.15 }} color={Styles.Colors.MEDIUM_GREY}>
        {tCommon('lastEdited')}: {formatAPITimestamp(version.revision_created_at)}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        opacity: !version ? 0.7 : 1,
        px: 2,
      }}>
      <Box
        sx={{
          flexBasis: '10%',
          display: 'flex',
          mr: 2,
        }}>
        {iconIndicator()}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Typography
          sx={{
            fontSize: '0.75rem', // 12px
            textTransform: 'uppercase',
            fontWeight: 'bold',
            color: labelColor(),
          }}>
          {label()}
        </Typography>

        {lastEditedAt()}
      </Box>
    </Box>
  );
};

const VersionActionMenu: FC<{
  pageStatus: DisplayStatus;
  publishDate?: string;
  versions: PageVersion[];
  onSelected: (version: PageVersion) => void;
  selectedVersionId: number;
}> = ({ pageStatus, publishDate, versions, onSelected, selectedVersionId }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(1);
  const open = Boolean(anchorEl);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (version: PageVersion, index: number) => {
    setSelectedIndex(index);
    onSelected(version);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectedVersion = versions.find((v) => v.id === selectedVersionId);

  return (
    <div>
      <List component='nav' aria-label='Device settings' sx={{ bgcolor: 'background.paper', p: 0 }}>
        <ListItemButton
          id='lock-button'
          aria-haspopup='listbox'
          aria-controls='lock-menu'
          aria-label='when device is locked'
          aria-expanded={open ? 'true' : undefined}
          sx={{
            borderRadius: 1,
            border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
            p: 0,
          }}
          onClick={handleClickListItem}>
          {selectedVersion ? (
            <VersionBox publishDate={publishDate} pageStatus={pageStatus} version={selectedVersion} />
          ) : (
            <span />
          )}
          <MaterialSymbol
            name='arrow_drop_down'
            sx={{
              color: Styles.Colors.MEDIUM_DARK_GREY,
              borderLeft: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
            }}
          />
        </ListItemButton>
      </List>
      <Menu
        id='lock-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'lock-button',
            role: 'listbox',
          },
        }}>
        {versions.map((option, index) => (
          <MenuItem
            key={option.id}
            selected={index === selectedIndex}
            onClick={() => handleMenuItemClick(option, index)}>
            <VersionBox pageStatus={pageStatus} version={option} />
          </MenuItem>
        ))}

        {versions.length === 1 && (
          <MenuItem disabled>
            <VersionBox publishDate={undefined} pageStatus={pageStatus} version={undefined} />
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export const PageEditorHeader: FC<
  Required<Pick<EditorHeaderProps, 'menuOpen' | 'onToggleMenuOpen' | 'onTogglePageSettingsOpen'>>
> = ({ menuOpen, onToggleMenuOpen, onTogglePageSettingsOpen }) => {
  const page = useEditorModel<Page>();
  const [pageVersions, setPageVersions] = useState<PageVersion[]>([]);

  useEffect(() => {
    Api.getPageVersions(page.id || 0)
      .fetchDirect(null)
      .then((res) => {
        if (res) {
          setPageVersions(res.versions);
        }
      });
  }, [page.id, page.updated_at]);

  const { setPages } = usePageEditorContext();
  const [, setParams] = useSearchParams();

  const displayStatus = useMemo(() => getDisplayStatus(page), [page]);

  const currentPageBreadcrumb = {
    title: page.title,
    id: page.id ?? 0,
    path: page.path ?? null,
    parent_id: page.parent_id ?? null,
    is_protected: page.is_protected ?? false,
    locked_by_parent: page.locked_by_parent ?? false,
  };

  return (
    <EditorHeader
      breadcrumbs={(page.breadcrumbs || []).concat(currentPageBreadcrumb)}
      menuOpen={menuOpen}
      onToggleMenuOpen={onToggleMenuOpen}
      onTogglePageSettingsOpen={onTogglePageSettingsOpen}
      setPages={setPages}
      showMenuButton
      pageVersions={pageVersions}
      versionDropdown={
        <VersionActionMenu
          pageStatus={displayStatus}
          versions={pageVersions}
          publishDate={formatAPITimestamp(page.published_at, 'date') ?? undefined}
          onSelected={(v) => {
            if (v.id !== page?.active_version_id) {
              setParams({ versionId: v.id!.toString() });
            }
          }}
          selectedVersionId={page?.active_version_id || 0}
        />
      }>
      <ImportedPageHint status={page.status} />
    </EditorHeader>
  );
};

export default PageEditorHeader;
